import { Box, Button, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { MdAddCircleOutline, MdOutlineVisibility } from "react-icons/md";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { TabActive } from "../../../../types/project.type";
import { useCreateBudgetFlow } from "../hooks/useCreateBudgetFlow";
import BudgetComparisonModal from "../BudgetComparisonModal";
import NegotiationComparisonModal from "../NegotiationComparisonModal";
import ClosingComparisonModal from "../ClosingComparisonModal";
import ImportServicesModal from "../ImportServicesModal";

const toolbarButtonSx = {
  height: 32,
  borderRadius: "8px",
  fontSize: 13,
  fontWeight: 400,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const,
  px: 1.5,
  flexShrink: 0,
  boxShadow: "none",
  bgcolor: "#FFF",
  borderColor: "#1361A4",
  color: "#1361A4",
  "&:hover": { bgcolor: "#EAF2FA", borderColor: "#1361A4", boxShadow: "none" },
};

/**
 * Ações do cabeçalho do orçamento: "Pré-visualizar" (o comparativo da etapa) e
 * "Novo Orçamento". Renderiza inline, à direita do título do bloco de pagamento
 * ("[A] Pagamento direto pelo cliente…") e do título da visão de Totais.
 *
 * Vieram da Lista de Orçamentos (barra lateral), que fica escondida quando o
 * painel está recolhido. Os modais de comparação são autocontidos — recebem a
 * lista e fazem a própria seleção —, então quem abre também os hospeda.
 *
 * Nenhuma das duas ações edita a versão exibida: seguem ativas com o orçamento
 * em somente leitura (aprovado, negociação antiga), diferente de Salvar/Aprovar.
 */
const BudgetToolbar = () => {
  const { budgetList } = useBudget();
  const { tabActive } = useProject();
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const newBudgetFlow = useCreateBudgetFlow();

  const closingComparisonBudgetList = useMemo(
    () =>
      (budgetList || []).filter((b) =>
        b.versionHistory?.some(
          (v: { status?: string }) =>
            v.status === "CLOSURE" ||
            v.status === "APPROVED" ||
            v.status === "AUDITED"
        )
      ),
    [budgetList]
  );

  const hasNegotiationToCompare = useMemo(
    () =>
      (budgetList || []).some((b) =>
        b.versionHistory?.some(
          (v: { status?: string }) => v.status === "NEGOTIATE"
        )
      ),
    [budgetList]
  );

  const preview = useMemo(() => {
    switch (tabActive) {
      case TabActive.BUDGET:
        return {
          enabled: (budgetList?.length ?? 0) >= 1,
          disabledHint: "É necessário ao menos 1 orçamento para pré-visualizar",
        };
      case TabActive.NEGOTIATION:
        return {
          enabled: hasNegotiationToCompare,
          disabledHint: "É necessário ao menos 1 negociação para comparar",
        };
      case TabActive.APPROVED:
        return {
          enabled: closingComparisonBudgetList.length >= 1,
          disabledHint: "É necessário ao menos 1 fechamento para comparar",
        };
      default:
        return { enabled: false, disabledHint: "" };
    }
  }, [
    tabActive,
    budgetList,
    hasNegotiationToCompare,
    closingComparisonBudgetList,
  ]);

  const isBudgetStage = [
    TabActive.BUDGET,
    TabActive.NEGOTIATION,
    TabActive.APPROVED,
  ].includes(tabActive);

  if (!isBudgetStage) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        ml: "auto",
        flexShrink: 0,
        flexWrap: "wrap",
      }}
    >
      <Tooltip title={preview.enabled ? "" : preview.disabledHint}>
        {/* span: botão desabilitado não dispara os eventos do Tooltip */}
        <span>
          <Button
            variant="outlined"
            onClick={() => setComparisonOpen(true)}
            disabled={!preview.enabled}
            startIcon={<MdOutlineVisibility size={16} />}
            sx={toolbarButtonSx}
          >
            Pré-visualizar
          </Button>
        </span>
      </Tooltip>

      {tabActive === TabActive.BUDGET && (
        <Button
          variant="outlined"
          onClick={newBudgetFlow.open}
          startIcon={<MdAddCircleOutline size={16} />}
          sx={toolbarButtonSx}
        >
          Novo Orçamento
        </Button>
      )}

      {tabActive === TabActive.BUDGET && (
        <>
          <BudgetComparisonModal
            isOpen={comparisonOpen}
            onClose={() => setComparisonOpen(false)}
            budgetList={budgetList || []}
          />
          <ImportServicesModal {...newBudgetFlow.modalProps} />
        </>
      )}
      {tabActive === TabActive.NEGOTIATION && (
        <NegotiationComparisonModal
          isOpen={comparisonOpen}
          onClose={() => setComparisonOpen(false)}
          budgetList={budgetList || []}
        />
      )}
      {tabActive === TabActive.APPROVED && (
        <ClosingComparisonModal
          isOpen={comparisonOpen}
          onClose={() => setComparisonOpen(false)}
          budgetList={closingComparisonBudgetList}
        />
      )}
    </Box>
  );
};

export default BudgetToolbar;
